import {
  ArrowLeft,
  BookIcon,
  BookTemplateIcon,
  Clipboard,
  ClipboardIcon,
  Flag,
  Info,
  LoaderCircle,
  Trash,
  Upload,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/use-toast";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";
import { createMicrosoftDocument } from "../services/createMicrosoftDocument";
import { createTemplate } from "../services/createTemplate";
import { deleteDocument } from "../services/deleteDocument";
import { cached, getPreviews } from "../services/getPreviews";
import { getSlides } from "../services/getSlides";
import { Preview } from "../types";
import { v4 } from "uuid";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";

type AddSlideType = "template" | "library" | null;

interface TemplateForm {
  name: string;
  description: string;
  googleSlideLink: string;
  resetTextBoxes: boolean;
}

const Documents: React.FC = () => {
  const [addSlideType, setAddSlideType] = useState<AddSlideType>(null);
  const [chosenPlatform, setChosenPlatform] = useState<"google" | "ppt" | null>(
    null
  );
  const [powerpointFile, setPowerpointFile] = useState<File | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAddNewFailed, setShowAddNewFailed] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  const [, copy] = useCopyToClipboard();
  const [slides, setSlides] = useState<Array<any>>([]);
  const [deletingItem, setDeletingItem] = useState<string>("");
  const [loadingSlides, setLoadingSlides] = useState<boolean>(false);

  const [savingTemplate, setSavingTemplate] = useState<boolean>(false);

  const navigate = useNavigate();

  const [previews, setPreviews] = useState<
    Array<Preview & { document_id: string }>
  >([]);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [confirmDeletePortalOpen, setConfirmDeletePortalOpen] =
    useState<string>("");
  const [deletingSlide, setDeletingSlide] = useState<boolean>(false);

  const [templateForm, setTemplateForm] = useState<TemplateForm>({
    name: "",
    description: "",
    googleSlideLink: "",
    resetTextBoxes: false,
  });

  async function addDeckFromTemplate() {
    if (!chosenPlatform) {
      toast({
        title: "Please select a platform",
      });
      return;
    }
    setSavingTemplate(true);

    const createDocumentResponse = await createTemplate({
      name: templateForm.name,
      description: templateForm.description,
      google_document_link: templateForm.googleSlideLink,
      document_type: "template",
      platform: chosenPlatform,
      reset_text_boxes: templateForm.resetTextBoxes,
    });

    if (!createDocumentResponse) {
      setShowAddNewFailed({
        message: createDocumentResponse.message,
        success: false,
      });
      setSavingTemplate(false);
      return;
    }

    if (createDocumentResponse && !createDocumentResponse.success) {
      setShowAddNewFailed({
        message: createDocumentResponse.message,
        success: false,
      });

      setDialogOpen((s) => {
        if (!s) {
          toast({
            title:
              "You need to share your the presentation with the worker emails",
            description: `Share the presentation with the following emails:\nflashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`,
            action: (
              <Button
                onClick={() =>
                  copy(
                    `flashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`
                  )
                }
                variant="secondary"
                className="p-2"
              >
                <Clipboard size={15} />
              </Button>
            ),
          });
        }
        return s;
      });

      setSavingTemplate(false);
      return;
    }

    setDialogOpen(false);
    await getSlidesAsync();
    setAddSlideType(null);

    setSavingTemplate(false);
  }

  async function addLibraryFromTemplate() {
    if (!chosenPlatform) {
      toast({
        title: "Please select a platform",
      });
      return;
    }

    setSavingTemplate(true);

    const createDocumentResponse = await createTemplate({
      google_document_link: templateForm.googleSlideLink,
      document_type: "library",
      platform: chosenPlatform,
      reset_text_boxes: templateForm.resetTextBoxes,
    });

    if (createDocumentResponse && !createDocumentResponse.success) {
      toast({
        title: "You need to share your the presentation with the worker emails",
        description: `Share the presentation with the following emails:\nflashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`,
        action: (
          <Button
            onClick={() =>
              copy(
                `flashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`
              )
            }
            variant="secondary"
            className="p-2"
          >
            <Clipboard size={15} />
          </Button>
        ),
      });
      setShowAddNewFailed(createDocumentResponse);
      setSavingTemplate(false);
      return;
    }

    setDialogOpen(false);
    await getSlidesAsync();
    setAddSlideType(null);

    setSavingTemplate(false);

    toast({
      title: "Library Created",
      description: "Library created successfully",
    });
  }

  const [creatingMicrosoftTemplate, setCreatingMicrosoftTemplate] =
    useState(false);

  async function createMicrosoftTemplate(type: "template" | "library") {
    if (!chosenPlatform || !powerpointFile) {
      toast({
        title: "Please select a platform and upload a PowerPoint file",
      });
      return;
    }
    setSavingTemplate(true);

    setCreatingMicrosoftTemplate(true);
    const newSlide = {
      id: `processing-${v4()}`,
      name: `Untitled [File: ${powerpointFile.name}]`,
      format: "microsoft",
      type,
      updated_at: new Date().toISOString(),
    };
    setSlides((prev) => [newSlide, ...prev]);

    const createDocumentResponse = await createMicrosoftDocument({
      name: templateForm.name,
      description: templateForm.description,
      document_type: type,
      file: powerpointFile,
      reset_text_boxes: templateForm.resetTextBoxes,
    });

    if (!createDocumentResponse) {
      setShowAddNewFailed({
        message: createDocumentResponse.message,
        success: false,
      });
      setSavingTemplate(false);
      setCreatingMicrosoftTemplate(false);
      setSlides((prev) => prev.filter((p) => p.id !== newSlide.id));
      return;
    }

    setDialogOpen(false);
    setCreatingMicrosoftTemplate(false);
    if (createDocumentResponse && !createDocumentResponse.success) {
      setShowAddNewFailed({
        message: createDocumentResponse.message,
        success: false,
      });
      setDialogOpen((s) => {
        if (!s) {
          toast({
            title: "Error uploading presentation",
            description: createDocumentResponse.message,
          });
          return s;
        }
        return s;
      });
      setSavingTemplate(false);
      setSlides((prev) => prev.filter((p) => p.id !== newSlide.id));
      return;
    }
    await getSlidesAsync();
    setAddSlideType(null);
    setSavingTemplate(false);
    setChosenPlatform(null);
    toast({
      title: "Template Created",
      description: "Template created successfully",
    });
  }

  async function getSlidesAsync() {
    setLoadingSlides(true);
    const slides = await getSlides();
    setSlides(slides);
    setLoadingSlides(false);
  }

  const slideIds = slides.map((s) => s.id);

  useEffect(() => {
    if (slides.length > 0) {
      const cachedPreviewFetcher = cached(getPreviews);
      slides
        .filter((s) => !s.id.startsWith("processing"))
        .forEach((slide) => {
          cachedPreviewFetcher(slide.id, 0, 1).then((d) => {
            setPreviews((prev) => {
              const newPreviews = [...prev];
              const index = newPreviews.findIndex((p) => p.id === slide.id);
              if (index === -1) {
                newPreviews.push({ ...d[0], document_id: slide.id });
              }
              return newPreviews;
            });
          });
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, JSON.stringify(slideIds)]);

  function getThumbnail(slideId: string) {
    const preview = previews.find((p) => p.document_id === slideId);
    return preview?.s3_preview_url || "";
  }

  useEffect(() => {
    async function asyncCall() {
      await getSlidesAsync();
    }
    asyncCall();
  }, []);

  useEffect(() => {
    if (!dialogOpen) {
      setAddSlideType(null);
      setSavingTemplate(false);
      setChosenPlatform(null);
      setPowerpointFile(null);
      setTemplateForm({
        description: "",
        name: "",
        googleSlideLink: "",
        resetTextBoxes: false,
      });
      setShowAddNewFailed(null);
    }
  }, [dialogOpen]);

  const confirmDeletePortal = createPortal(
    <div
      className="absolute bg-zinc-700/80 w-full h-full left-0 right-0 bottom-0"
      style={{ top: window.scrollY }}
      onClick={() => {
        setConfirmDeletePortalOpen("");
      }}
    >
      {confirmDeletePortalOpen !== "" && (
        <div
          className="bg-white absolute top-1/2 left-1/2 p-5 rounded-md min-w-[500px]"
          onClick={(e) => e.stopPropagation()}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <div className="flex flex-col items-start gap-5">
            <div>Are you sure you want to delete this presentation?</div>
            <div className="flex justify-end mt-4 w-full">
              <Button
                className="flex items-center gap-2 disabled:opacity-50 bg-white border border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                disabled={deletingSlide}
                onClick={async () => {
                  setDeletingSlide(true);
                  setDeletingItem(confirmDeletePortalOpen);
                  await deleteDocument(confirmDeletePortalOpen);
                  await getSlidesAsync();
                  setDeletingItem("");
                  setDeletingSlide(false);

                  toast({
                    title: "Slide Deleted",
                    description: "Slide deleted successfully",
                  });
                  setConfirmDeletePortalOpen("");
                }}
              >
                Delete
                {deletingSlide && (
                  <LoaderCircle size={15} className="animate-spin" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.getElementById("portal")!
  );

  return (
    <div className="px-10 w-full text-zinc-700">
      {confirmDeletePortalOpen && confirmDeletePortal}
      <div>
        {/* <h1 className="mt-[50px] text-2xl">Presentations</h1> */}
        <p className="text-zinc-500 mt-[50px]">
          Manage the template & library configurations that your team can access
          through FlashDocs. Contact{" "}
          <a className="text-[#0d33f1]" href="mailto:morten@flashdocs.ai">
            morten@flashdocs.ai
          </a>{" "}
          if you need any help.
        </p>
      </div>

      <div className="w-full flex items-center justify-between mt-4 gap-3">
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-[600px]"
          placeholder="Search"
        />
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
          }}
        >
          <DialogTrigger>
            <Button className="shadow-md">Add New Presentation</Button>
          </DialogTrigger>
          <DialogContent className="min-w-[650px] text-zinc-700 overflow-x-hidden overflow-y-scroll max-h-[800px] no-scrollbar">
            <DialogHeader className="border-b py-5">
              <DialogTitle className="font-medium">
                <div className="flex items-center gap-3">
                  {chosenPlatform && (
                    <div>
                      <button
                        onClick={() => {
                          if (addSlideType && chosenPlatform) {
                            setAddSlideType(null);
                          } else if (chosenPlatform) {
                            setChosenPlatform(null);
                          }
                        }}
                        className="p-2 rounded-md hover:bg-zinc-200 transition-all"
                      >
                        <ArrowLeft size={20} />
                      </button>
                    </div>
                  )}
                  <div>
                    Add{" "}
                    {!!chosenPlatform && addSlideType === "library"
                      ? "a library"
                      : !!chosenPlatform && addSlideType === "template"
                      ? "a template"
                      : "Slide"}
                  </div>
                </div>
              </DialogTitle>
              <DialogDescription>
                {!!chosenPlatform &&
                  addSlideType === "library" &&
                  "A library that allows users to prompt a slide or a deck with many slides. FlashDocs will then find the right slides in your library"}
                {!!chosenPlatform &&
                  addSlideType === "template" &&
                  "A template is a pre-defined deck with a fixed structure. Ex: Sales Deck for SMB Prospects, QBR Deck for FinTech clients, etc."}
                {chosenPlatform === null && "Choose the presentation type"}
              </DialogDescription>
            </DialogHeader>

            {addSlideType === null && !!chosenPlatform && (
              <div className="py-5">
                <p>What type of slide would you like to add to FlashDocs?</p>

                <div className="flex items-start justify-between gap-10 px-3 py-4 my-4 h-[200px]">
                  <div
                    role="button"
                    className="h-full flex-1 border rounded-md px-2 py-4 flex flex-col items-center justify-center gap-3 hover:bg-zinc-100 transition-all"
                    onClick={() => setAddSlideType("template")}
                  >
                    <BookTemplateIcon className="text-[#0D33F1]" size={24} />
                    <h4 className="text-md font-medium">Template</h4>
                    <p className="text-sm text-zinc-500 text-center">
                      A template is a pre-defined deck with a fixed structure.
                      It could be a sales deck or your standard QBR deck.
                    </p>
                  </div>
                  <div
                    role="button"
                    className="h-full flex-1 border rounded-md px-2 py-4 flex flex-col items-center justify-center gap-3 hover:bg-zinc-100 transition-all"
                    onClick={() => setAddSlideType("library")}
                  >
                    <BookIcon className="text-[#0D33F1]" size={24} />
                    <h4 className="text-md font-medium">Library</h4>
                    <p className="text-sm text-zinc-500 text-center">
                      A library is the full collection of all the slides you'd
                      want the teams to be able to access.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {addSlideType === "template" && chosenPlatform === "google" && (
              <div className="py-1">
                {!savingTemplate ? (
                  <>
                    {showAddNewFailed && showAddNewFailed.message && (
                      <div className="text-red-500 bg-red-100 p-3 mb-4 rounded-md">
                        <div>{showAddNewFailed.message}</div>
                      </div>
                    )}
                    <div className="mb-7">
                      <label htmlFor="name-template">Name</label>
                      <Input
                        id="name-template"
                        placeholder="Enter the name of deck"
                        value={templateForm.name}
                        onChange={(e) =>
                          setTemplateForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mb-7">
                      <label htmlFor="description-template">Description</label>
                      <Textarea
                        id="description-template"
                        placeholder="Write a description about deck"
                        value={templateForm.description}
                        onChange={(e) =>
                          setTemplateForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mb-7">
                      <label htmlFor="google-slide-link-template">
                        Google Slide Link
                      </label>
                      <Input
                        id="google-slide-link-template"
                        placeholder="Insert the link to the deck"
                        value={templateForm.googleSlideLink}
                        onChange={(e) =>
                          setTemplateForm((prev) => ({
                            ...prev,
                            googleSlideLink: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <div>
                        <input
                          checked={templateForm.resetTextBoxes}
                          onChange={(e) => {
                            setTemplateForm((prev) => ({
                              ...prev,
                              resetTextBoxes: e.target.checked,
                            }));
                          }}
                          type="checkbox"
                          name="reset-text-boxes"
                          id=""
                        />
                      </div>
                      <div>
                        <Tooltip>
                          <TooltipTrigger>Reset Text Boxes</TooltipTrigger>
                          <TooltipContent
                            side="right"
                            data-state="instant-open"
                            className="w-[350px] bg-white"
                          >
                            <p className="text-medium">Info</p>
                            <p className="text-zinc-500">
                              If checked, our system overwrites all text boxes
                              in the presentation and makes them standardized
                              placeholders: title/subtitle/paragraph/number/etc.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="bg-zinc-100 rounded-md p-3 my-4 border">
                      <div className="flex items-center justify-start gap-2">
                        <Info size={15} /> <p>Info</p>
                      </div>
                      <div className="text-zinc-400">
                        Remember: share your presentation with the following
                        email accounts:
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 my-3">
                            {[
                              "flashy@flashdocs.ai",
                              "flashy1@flashdocs.ai",
                              "flashy2@flashdocs.ai",
                              "flashy3@flashdocs.ai",
                            ].join(", ")}
                          </div>
                          <button
                            onClick={() => {
                              copy(
                                `flashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`
                              );
                              toast({
                                title: "Copied to clipboard",
                              });
                            }}
                            className="mx-2 border border-[#0d33f1] text-[#0d33f1] p-1 rounded-md hover:opacity-50 transition-all"
                          >
                            <ClipboardIcon size={20} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-5">
                      <Button
                        className="flex items-center gap-2"
                        onClick={addDeckFromTemplate}
                        disabled={savingTemplate}
                      >
                        <p>Done</p>
                        {savingTemplate && (
                          <LoaderCircle size={15} className="animate-spin" />
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="w-full py-4">
                    <div className="h-[10px] w-full rounded-full bg-[#0d33f1]/50 animate-pulse"></div>
                    <div className="text-center w-full py-2 text-zinc-400">
                      Your document is being uploaded. This may take a few
                      minutes
                    </div>
                  </div>
                )}
              </div>
            )}

            {!chosenPlatform && (
              <div className="py-5">
                <div className="flex items-start justify-between gap-10 px-3 py-4 my-4 h-[200px]">
                  <div
                    role="button"
                    className="h-full flex-1 border rounded-md px-2 py-4 flex flex-col items-center justify-center gap-3 hover:bg-zinc-100 transition-all"
                    onClick={() => setChosenPlatform("google")}
                  >
                    <img
                      className="w-8 aspect-auto"
                      src="/google_slides.png"
                      alt="google slides"
                    />
                    <h4 className="text-md font-medium">Google Slides</h4>
                  </div>
                  <div
                    role="button"
                    className="h-full flex-1 border rounded-md px-2 py-4 flex flex-col items-center justify-center gap-3 hover:bg-zinc-100 transition-all"
                    onClick={() => setChosenPlatform("ppt")}
                  >
                    <img
                      className="w-8 aspect-auto"
                      src="/powerpoint_logo.png"
                      alt="microsoft powerpoint logo"
                    />
                    <h4 className="text-md font-medium">PowerPoint</h4>
                  </div>
                </div>
              </div>
            )}

            {addSlideType === "library" && chosenPlatform === "google" && (
              <div className="my-5">
                {!savingTemplate ? (
                  <>
                    <div className="mb-7">
                      <label htmlFor="google-slide-link-template">
                        Google Slide Link
                      </label>
                      <Input
                        value={templateForm.googleSlideLink}
                        onChange={(e) =>
                          setTemplateForm((prev) => ({
                            ...prev,
                            googleSlideLink: e.target.value,
                          }))
                        }
                        id="google-slide-link-template"
                        placeholder="Insert the link to the deck"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <div>
                        <input
                          checked={templateForm.resetTextBoxes}
                          onChange={(e) => {
                            setTemplateForm((prev) => ({
                              ...prev,
                              resetTextBoxes: e.target.checked,
                            }));
                          }}
                          type="checkbox"
                          name="reset-text-boxes"
                        />
                      </div>
                      <div>
                        <Tooltip>
                          <TooltipTrigger>Reset Text Boxes</TooltipTrigger>
                          <TooltipContent
                            side="right"
                            data-state="instant-open"
                            className="w-[350px] bg-white"
                          >
                            <p className="text-medium">Info</p>
                            <p className="text-zinc-500">
                              If checked, our system overwrites all text boxes
                              in the presentation and makes them standardized
                              placeholders: title/subtitle/paragraph/number/etc.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="bg-zinc-100 rounded-md p-3 my-4 border">
                      <div className="flex items-center justify-start gap-2">
                        <Info size={15} /> <p>Info</p>
                      </div>
                      <div className="text-zinc-400">
                        Remember: share your presentation with the following
                        email accounts:
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 my-3">
                            {[
                              "flashy@flashdocs.ai",
                              "flashy1@flashdocs.ai",
                              "flashy2@flashdocs.ai",
                              "flashy3@flashdocs.ai",
                            ].join(", ")}
                          </div>
                          <button
                            onClick={() => {
                              copy(
                                `flashy@flashdocs.ai\nflashy1@flashdocs.ai\nflashy2@flashdocs.ai\nflashy3@flashdocs.ai\n`
                              );

                              toast({
                                title: "Copied to clipboard",
                              });
                            }}
                            className="mx-2 border border-[#0d33f1] text-[#0d33f1] p-1 rounded-md hover:opacity-50 transition-all"
                          >
                            <ClipboardIcon size={20} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-5">
                      <Button
                        onClick={() => {
                          addLibraryFromTemplate();
                        }}
                      >
                        Add Master Library
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="w-full py-4">
                    <div className="h-[10px] w-full rounded-full bg-[#0d33f1]/50 animate-pulse"></div>
                    <div className="text-center w-full py-2 text-zinc-400">
                      Your document is being uploaded. This may take a few
                      minutes
                    </div>
                  </div>
                )}
              </div>
            )}

            {(addSlideType === "library" || addSlideType === "template") &&
              chosenPlatform === "ppt" && (
                <div className="my-3">
                  {!savingTemplate ? (
                    <>
                      {showAddNewFailed && showAddNewFailed.message && (
                        <div className="text-red-500 bg-red-100 p-3 mb-4 rounded-md">
                          <div>{showAddNewFailed.message}</div>
                        </div>
                      )}
                      <div className="mb-7">
                        {addSlideType === "template" && (
                          <div className="flex flex-col items-center justify-center gap-3 w-full mb-3">
                            <div className="w-full">
                              <label htmlFor="template name">Name</label>
                              <input
                                value={templateForm.name}
                                onChange={(e) => {
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }));
                                }}
                                type="text"
                                className="w-full border rounded-md p-2"
                                placeholder="Name"
                              />
                            </div>
                            <div className="w-full">
                              <label htmlFor="template name">Description</label>
                              <textarea
                                value={templateForm.description}
                                onChange={(e) => {
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                  }));
                                }}
                                className="w-full border rounded-md p-2"
                                placeholder="Description"
                              />
                            </div>
                          </div>
                        )}
                        <label htmlFor="powerpoint-upload">
                          PowerPoint Upload
                        </label>
                        <div className="w-full border border-dashed h-[200px] bg-zinc-100 rounded-md relative hover:bg-[#0033df]/10 transition-all">
                          <input
                            className="w-full h-full opacity-0 cursor-pointer relative z-10"
                            id="powerpoint-upload"
                            type="file"
                            onChange={(e) => {
                              const files = e.target.files;
                              if (!files) return;
                              setPowerpointFile(files[0]);
                            }}
                          />
                          {creatingMicrosoftTemplate ? (
                            <div className="absolute inset-0 flex items-center justify-center gap-2 text-zinc-500">
                              <p>Creating Microsoft Template</p>
                            </div>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center gap-2 text-zinc-500">
                              <Upload />
                              {powerpointFile ? (
                                <p className="text-sm">{powerpointFile.name}</p>
                              ) : (
                                <p>Upload PowerPoint</p>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-3 my-3">
                            <div>
                              <input
                                checked={templateForm.resetTextBoxes}
                                onChange={(e) => {
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    resetTextBoxes: e.target.checked,
                                  }));
                                }}
                                type="checkbox"
                                name="reset-text-boxes"
                              />
                            </div>
                            <div>
                              <Tooltip>
                                <TooltipTrigger>
                                  Reset Text Boxes
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  data-state="instant-open"
                                  className="w-[350px] bg-white"
                                >
                                  <p className="text-medium">Info</p>
                                  <p className="text-zinc-500">
                                    If checked, our system overwrites all text
                                    boxes in the presentation and makes them
                                    standardized placeholders:
                                    title/subtitle/paragraph/number/etc.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end mt-5">
                        <Button
                          disabled={
                            !powerpointFile || creatingMicrosoftTemplate
                          }
                          onClick={async () => {
                            await createMicrosoftTemplate(addSlideType);
                            // setChosenPlatform(null);
                            // setAddSlideType(null);
                          }}
                        >
                          {creatingMicrosoftTemplate && (
                            <LoaderCircle className="animate-spin" size={20} />
                          )}
                          Add PowerPoint{" "}
                          {addSlideType === "library"
                            ? "Master Library"
                            : "Template"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="w-full py-4">
                      <div className="h-[10px] w-full rounded-full bg-[#0d33f1]/50 animate-pulse"></div>
                      <div className="text-center w-full py-2 text-zinc-400">
                        Your document is being uploaded. This may take a few
                        minutes
                      </div>
                    </div>
                  )}
                </div>
              )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="w-full py-10">
        {loadingSlides && (
          <div className="w-full flex items-center justify-center my-3">
            <Spinner />
          </div>
        )}

        {slides.length === 0 && !loadingSlides && (
          <div className="p-3 w-full border border-[#0D33F1] mt-10 rounded-md shadow-md bg-[#0D33F1]/5">
            <div className="flex gap-3 items-center justify-start">
              <Flag className="text-[#0D33F1]" size={20} />
              <h2 className="text-lg font-medium">Get Started with slides</h2>
            </div>

            <div className="mt-2">
              <p className="text-sm text-zinc-500">
                Build out your template library with both a library of slides
                that users can mix and match as well as pre-defined deck
                templates, so it's easy to find the QBR template or sales deck
                template.
              </p>
            </div>
          </div>
        )}

        {slides.length > 0 && (
          <div className="border rounded-md">
            <Table>
              <TableHeader className="bg-zinc-100">
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead className="w-1/4">Name</TableHead>
                  <TableHead className="flex items-center justify-center">
                    Format
                  </TableHead>
                  {/* <TableHead>Created by</TableHead> */}
                  <TableHead>Last updated at</TableHead>
                  <TableHead>Type</TableHead>
                  {/* <TableHead>Status</TableHead> */}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slides
                  .filter((s) => s.id !== deletingItem)
                  .filter((s) =>
                    searchQuery
                      ? s.name.match(new RegExp(searchQuery, "ig"))
                      : true
                  )
                  .map((slide, i) => (
                    <TableRow
                      key={i}
                      onClick={() => navigate(`/document/${slide.id}#Slides`)}
                    >
                      <TableCell role="button" className="text-center">
                        {!slide.id.startsWith("processing") &&
                        getThumbnail(slide.id) ? (
                          <img
                            className="border rounded-md w-full max-w-[100px] aspect-video m-auto min-w-[80px]"
                            src={getThumbnail(slide.id)}
                            alt="slide"
                          />
                        ) : (
                          <div className="min-w-[80px] max-w-[100px] aspect-video m-auto bg-zinc-200 animate-pulse"></div>
                        )}
                      </TableCell>
                      <TableCell
                        role="button"
                        className={cn(
                          "font-medium w-1/4",
                          slide.id.startsWith("processing") &&
                            "text-zinc-500 animate-pulse"
                        )}
                      >
                        <p>{slide.name}</p>
                        {slide.id.startsWith("processing") && (
                          <p className="text-xs text-zinc-500">Processing...</p>
                        )}
                      </TableCell>
                      <TableCell role="button">
                        {slide.format === "google" ? (
                          <div className="flex items-center gap-2">
                            <img
                              className="w-4"
                              src="/google_slides.png"
                              alt="google logo"
                            />
                            <p>Google Slides</p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <img
                              className="w-4"
                              src="/powerpoint_logo.png"
                              alt="powerpoint logo"
                            />
                            <p>PowerPoint</p>
                          </div>
                        )}
                      </TableCell>
                      {/* <TableCell>{slide.created_by || "unknown"}</TableCell> */}
                      <TableCell>
                        {new Date(slide.updated_at).toDateString()}
                      </TableCell>
                      <TableCell>
                        {slide.type === "template" ? (
                          <div className=" bg-[#033df1]/5 text-xs w-fit px-2 text-[#033df1] py-0.5 rounded-full border border-[#033df1]">
                            Template
                          </div>
                        ) : (
                          <div className="bg-green-300/5 text-xs w-fit px-2 text-green-600 py-0.5 rounded-full border border-green-500">
                            Library
                          </div>
                        )}
                      </TableCell>
                      {/* <TableCell>
                        {slide.status === "in_progress" ? (
                          <div className="flex items-center gap-3">
                            <p className="text-[#033df1]">Processing</p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 text-green-700 font-medium">
                            <Check size={20} />
                            <div>Live</div>
                          </div>
                        )}
                      </TableCell> */}

                      {slide.type === "template" ? (
                        <TableHead>
                          <button
                            type="button"
                            className="p-2 rounded-md border hover:bg-zinc-100 transition-all"
                            onClick={async (e) => {
                              e.stopPropagation();

                              setConfirmDeletePortalOpen(slide.id);
                            }}
                          >
                            <div className="hidden">Delete</div>
                            <Trash size={15} />
                          </button>
                        </TableHead>
                      ) : (
                        <>
                          {slide.type === "library" &&
                            slides.filter((s) => s.type === "library").length >
                              1 && (
                              <TableHead>
                                <button
                                  type="button"
                                  className="p-2 rounded-md border hover:bg-zinc-100 transition-all"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setConfirmDeletePortalOpen(slide.id);
                                  }}
                                >
                                  <div className="hidden">Delete</div>
                                  <Trash size={15} />
                                </button>
                              </TableHead>
                            )}
                        </>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};
export default Documents;
